/* global G, ENV, PLATFORM */
import '@formatjs/intl'
import '@formatjs/intl-locale/polyfill'
import '@formatjs/intl-pluralrules/polyfill'
import settings from '@tenant/settings'
import i18n from 'i18next'
import HttpApi from 'i18next-http-backend'
import { API_URL } from 'lib/util/environment'
import api from './api'
import disabled from './api/disabled'

/**
 * A list of namespaces that should be used for every tenant.
 *
 * @type {string[]}
 */
const defaultNamespaces = [
  'common',
  'custom',
  'generic',
  'device',
  'guest',
  'native',
  'notification',
  'organisation',
  'person',
  'sidebar',
  'user',
]

/**
 * Returns a base configuration of {@link i18n} that should be used
 * regardless of the currently running platform.
 *
 * @param {Gaia.Web.Application|Gaia.Mobile.Application} obj
 * @returns {import('i18next').InitOptions}
 */
const baseConfig = obj => ({
  // languages
  lng: 'en',
  fallbackLng: 'en',
  supportedLngs: settings.languages,

  // namespaces
  ns: [
    ...defaultNamespaces,
    ...settings.namespaces,
  ],
  fallbackNS: 'common',

  interpolation: {
    escapeValue: false,
    skipOnVariables: false,
  },

  // debug: true, // turn this on to enable debug information about missing translations (very useful)
  updateMissing: false, // required for missingKeyHandler
  saveMissing: false, // required for missingKeyHandler
  missingKeyHandler: (...args) => obj[G.ADAPTER][G.INTL].missing(...args),
})

/**
 * Web specific configuration
 * @type {import('i18next').InitOptions}
 */
const webConfig = {
  backend: {
    loadPath: `${API_URL}/intl/{{lng}}/{{ns}}.json`,
  },
}

/**
 * Mobile specific configuration
 * @type {import('i18next').InitOptions}
 */
const mobileConfig = {}

/**
 * Full configuration
 *
 * @param {Gaia.Web.Application|Gaia.Mobile.Application} obj
 * @returns {import('i18next').InitOptions}
 */
const intlConfig = obj => ({
  ...baseConfig(obj),
  ...PLATFORM === 'web' ? webConfig : {},
  ...PLATFORM === 'mobile' ? mobileConfig : {},
})

/**
 * Translation Manager
 *
 * Initializes the translation manager, respecting the `PLATFORM` and `ENV`.
 *
 * @param {Gaia.Web.Application|Gaia.Mobile.Application} obj
 * @returns {(function(): Readonly<{}>)|*|(function(): Promise<*>)}
 */
const translationManager = obj => (!obj[G.PROPS].i18n ? disabled : async () => {
  const bootstrappedManager = (PLATFORM === 'web' && i18n.use(HttpApi))
    || (PLATFORM === 'mobile' && i18n)
    || i18n
  await bootstrappedManager.init(intlConfig(obj))

  return api(i18n)(obj)
})

export default translationManager
